import { Directive, Input, ElementRef, AfterViewInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import { debounceTime } from 'rxjs/operators';
import { Subscription, Subject, fromEvent, timer } from 'rxjs';
import * as d3Drag from 'd3-drag';
import * as d3Selection from 'd3-selection';
import { DragBehavior } from 'd3-drag';
import { floorDecimal, getElementStylePosition, getElementStyleSize, roundDecimal } from './util';
import { ElDada } from './ElDada';

/** 拖拉定位工具 */
@Directive({
  selector: '[tmDragPosition]',
  exportAs: 'dragPosition'
})
export class DragPositionDirective implements AfterViewInit, OnDestroy {
  /** 拖曳容器範圍，若帶入祖父元素以上容器，需確保中間沒有設為相對位置的元素，否則會有位移產生 */
  @Input() container: HTMLElement;
  data: ElDada;
  /** 初始數據 */
  @Input() initData: ElDada;
  /** 數據初始化完成 */
  dataInit$ = new Subject<ElDada>();
  /** 數據初始化完成事件 */
  @Output() dpDataInit = new EventEmitter<ElDada>();
  /** 位置拖曳行為完成事件 */
  @Output() positionDragged = new EventEmitter<ElDada>();
  /** 位置拖曳行為 */
  private dragPosition: DragBehavior<Element, ElDada, any>;
  /** 螢幕大小縮放自動去更新位置 */
  private resizeSubc: Subscription;
  /** 是否有組合 tmDragSize 一起使用 */
  @Input() hasTmDragSize = false;
  /** 只能做水平移動，預設為否 */
  @Input() onlyXAxis = false;
  /** 只能做垂直移動，預設為否 */
  @Input() onlyYAxis = false;

  constructor(private target: ElementRef) { }

  ngAfterViewInit(): void {
    if (!this.container) {
      this.container = this.target.nativeElement.parentElement;
    }

    const currPos = getElementStylePosition(this.target.nativeElement);
    d3Selection.select(this.container)
    .style('position', 'relative');

    d3Selection.select(this.target.nativeElement)
    .style('cursor', this.onlyXAxis || this.onlyYAxis ? 'grab' : 'move')
    .style('position', 'absolute')
    .style('box-sizing', 'border-box');

    this.dataInit$.subscribe(data => {
      if (!this.hasTmDragSize) {
        this.data = data;
        this.setDragBehavior();
        d3Selection.select(this.target.nativeElement)
        .data([this.data])
        .call(this.dragPosition);

        this.resizeSubc = fromEvent(window, 'resize')
        .pipe(debounceTime(15))
        .subscribe(evt => {
          this.reSizeLocatePosition();
        });
      }
    });

    timer(0).subscribe(t => {
      const currSize = getElementStyleSize(this.target.nativeElement);
      let data: ElDada;
      if (this.initData) {
        data = {
          x: this.initData.percentX * this.container.clientWidth / 100,
          y: this.initData.percentY * this.container.clientHeight / 100,
          w: currSize.w,
          h: currSize.h,
          percentX: this.initData.percentX,
          percentY: this.initData.percentY,
          minX: this.initData.minX * this.container.clientWidth / this.initData.posContainerWidth,
          minY: this.initData.minY * this.container.clientHeight / this.initData.posContainerHeight,
          maxX: this.initData.maxX * this.container.clientWidth / this.initData.posContainerWidth,
          maxY: this.initData.maxY * this.container.clientHeight / this.initData.posContainerHeight,
          posContainer: this.container,
          posContainerWidth: this.container.clientWidth,
          posContainerHeight: this.container.clientHeight,
        };
      } else {
        const isSmallX = this.container.clientWidth > currSize.w;
        const isSmallY = this.container.clientHeight > currSize.h;
        data = {
          x: currPos.left,
          y: currPos.top,
          w: currSize.w,
          h: currSize.h,
          percentX: roundDecimal(currPos.left * 100 / this.container.clientWidth, 5),
          percentY: roundDecimal(currPos.top * 100 / this.container.clientHeight, 5),
          minX: isSmallX ? 0 : Math.floor(this.container.clientWidth - currSize.w),
          minY: isSmallY ? 0 : Math.floor(this.container.clientHeight - currSize.h),
          maxX: isSmallX ? Math.floor(this.container.clientWidth - currSize.w) : 0,
          maxY: isSmallY ? Math.floor(this.container.clientHeight - currSize.h) : 0,
          posContainer: this.container,
          posContainerWidth: this.container.clientWidth,
          posContainerHeight: this.container.clientHeight,
        };
      }
      if (data.maxX <= data.minX) { data.maxX = data.minX; }
      if (data.maxY <= data.minY) { data.maxY = data.minY; }

      data.x = data.x > data.maxX ? data.maxX : (data.x < data.minX ? data.minX : data.x);
      data.y = data.y > data.maxY ? data.maxY : (data.y < data.minY ? data.minY : data.y);

      d3Selection.select(this.target.nativeElement)
      .style('top', data.y + 'px')
      .style('left', data.x + 'px');
      this.dataInit$.next(data);
      this.dpDataInit.emit(this.data);
    });
  }

  ngOnDestroy(): void {
    if (this.resizeSubc) { this.resizeSubc.unsubscribe(); }
  }

  private reSizeLocatePosition() {
    const canX = !this.onlyYAxis;
    const canY = !this.onlyXAxis;
    const currSize = getElementStyleSize(this.target.nativeElement);
    this.data.w = currSize.w;
    this.data.h = currSize.h;
    this.data.posContainerWidth = this.data.posContainer.clientWidth;
    this.data.posContainerHeight = this.data.posContainer.clientHeight;
    const isSmallX = this.data.posContainerWidth > this.data.w;
    const isSmallY = this.data.posContainerHeight > this.data.h;

    this.data.minX = isSmallX ? 0 : Math.floor(this.data.posContainerWidth - this.data.w);
    this.data.maxX = isSmallX ? Math.floor(this.data.posContainerWidth - this.data.w) : 0;
    if (this.data.maxX < this.data.minX) { this.data.maxX = this.data.minX; }
    this.data.minY = isSmallY ? 0 : Math.floor(this.data.posContainerHeight - this.data.h);
    this.data.maxY = isSmallY ? Math.floor(this.data.posContainerHeight - this.data.h) : 0;
    if (this.data.maxY < this.data.minY) { this.data.maxY = this.data.minY; }

    // 根據比例重算位置，並最多吻合邊界
    if (canX) {
      this.data.x = floorDecimal(this.data.percentX * this.data.posContainer.clientWidth / 100, 5);
      this.data.x = this.data.x > this.data.maxX ? this.data.maxX : (this.data.x < this.data.minX ? this.data.minX : this.data.x);
    }
    if (canY) {
      this.data.y = floorDecimal(this.data.percentY * this.data.posContainer.clientHeight / 100, 5);
      this.data.y = this.data.y > this.data.maxY ? this.data.maxY : (this.data.y < this.data.minY ? this.data.minY : this.data.y);
    }

    if (canX) {
      d3Selection.select(this.target.nativeElement).style('left', this.data.x + 'px');
    }
    if (canY) {
      d3Selection.select(this.target.nativeElement).style('top', this.data.y + 'px');
    }
  }

  private setDragBehavior() {
    const that = this;
    this.dragPosition = d3Drag.drag<Element, ElDada>()
    .on('start', function() { onDragPosStart(this); })
    .on('drag', function(d) {
      onDraggingPosXY(this, d, that.onlyXAxis, that.onlyYAxis);
      if (that.onlyXAxis || that.onlyYAxis) {
        d3Selection.select(that.target.nativeElement)
        .style('cursor', 'grabbing');
      }
      that.positionDragged.emit(that.data);
    })
    .on('end', function() {
      onDragPosEnd(this);
      if (that.onlyXAxis || that.onlyYAxis) {
        d3Selection.select(that.target.nativeElement)
        .style('cursor', 'grab');
      }
    });
  }
}

/** 當拖曳事件發生 */
export function onDragPosStart(target: Element) {
  d3Selection.select(target)
  .classed('dragging', true);
}

/** 當拖曳事件結束 */
export function onDragPosEnd(target: Element) {
  d3Selection.select(target)
  .classed('dragging', false);
}

/** 當元素拖曳中 */
export function onDraggingPos(target: Element, d: ElDada) {
  d.x = d3Selection.event.x > d.maxX ? d.maxX : (d3Selection.event.x < d.minX ? d.minX : d3Selection.event.x);
  d.percentX = roundDecimal(d.x * 100 / d.posContainer.clientWidth, 5);
  d.y = d3Selection.event.y > d.maxY ? d.maxY : (d3Selection.event.y < d.minY ? d.minY : d3Selection.event.y);
  d.percentY = roundDecimal(d.y * 100 / d.posContainer.clientHeight, 5);
  d3Selection.select(target)
  .style('top', d.y + 'px')
  .style('left', d.x + 'px');
}

/** 當元素拖曳中，有X軸Y軸線制版本 */
export function onDraggingPosXY(target: Element, d: ElDada, onlyXAxis: boolean, onlyYAxis: boolean) {
  const canX = !onlyYAxis;
  const canY = !onlyXAxis;
  // console.log('event: ' + JSON.stringify(d3Selection.event));
  if (canX) {
    d.x = d3Selection.event.x > d.maxX ? d.maxX : (d3Selection.event.x < d.minX ? d.minX : d3Selection.event.x);
    d.percentX = roundDecimal(d.x * 100 / d.posContainer.clientWidth, 5);
  }
  if (canY) {
    d.y = d3Selection.event.y > d.maxY ? d.maxY : (d3Selection.event.y < d.minY ? d.minY : d3Selection.event.y);
    d.percentY = roundDecimal(d.y * 100 / d.posContainer.clientHeight, 5);
  }
  if (canX) {
    d3Selection.select(target).style('left', d.x + 'px');
  }
  if (canY) {
    d3Selection.select(target).style('top', d.y + 'px');
  }
}
